import type { DraftAdapter, DraftData, PetitionDraft } from './types';

type DraftReadHit = Readonly<{ kind: 'hit'; adapterIndex: number; draft: PetitionDraft }>;
type DraftReadMiss = Readonly<{ kind: 'miss'; adapterIndex: number }>;
type DraftReadError = Readonly<{ kind: 'error'; adapterIndex: number; error: Error }>;
type DraftReadResult = DraftReadHit | DraftReadMiss | DraftReadError;

type DraftSelection = Readonly<{ adapterIndex: number; draft: PetitionDraft }>;

export class DraftClient implements DraftAdapter {
	private readonly adapters: DraftAdapter[];

	constructor(adapters: DraftAdapter[]) {
		if (adapters.length === 0) {
			throw new Error('At least one adapter must be provided');
		}
		this.adapters = adapters;
	}

	async createDraft(authorId: string, data?: Partial<DraftData>): Promise<PetitionDraft> {
		return this.writeWithFallback(async (adapter) => adapter.createDraft(authorId, data));
	}

	async getDraft(authorId: string): Promise<PetitionDraft | null> {
		const results = await this.collectDraftResults(authorId);
		const selected = DraftClient.selectMostRecentDraft(results);

		if (!selected) return null;

		await this.repairPrimaryFromSelection(authorId, selected);
		return selected.draft;
	}

	async updateDraft(authorId: string, updates: Partial<PetitionDraft>): Promise<PetitionDraft> {
		return this.writeWithFallback(async (adapter) => adapter.updateDraft(authorId, updates));
	}

	async findOrCreateDraft(authorId: string, data?: Partial<DraftData>): Promise<PetitionDraft> {
		const draft = await this.getDraft(authorId);
		if (draft) return draft;

		return this.writeWithFallback(async (adapter) => adapter.createDraft(authorId, data));
	}

	private async collectDraftResults(authorId: string): Promise<readonly DraftReadResult[]> {
		const results = await Promise.all(
			this.adapters.map(async (adapter, index): Promise<DraftReadResult> => {
				try {
					const draft = await adapter.getDraft(authorId);
					return draft ? { adapterIndex: index, kind: 'hit', draft } : { adapterIndex: index, kind: 'miss' };
				} catch (error) {
					const adapterError = error instanceof Error ? error : new Error(String(error));
					return { adapterIndex: index, kind: 'error', error: adapterError };
				}
			}),
		);

		const failedResults = results.filter((result): result is DraftReadError => result.kind === 'error');
		if (failedResults.length === this.adapters.length) {
			const errorMessages = failedResults.map((result) => result.error.message);
			throw new Error(`All adapters failed: ${errorMessages.join(' | ')}`);
		}

		return results;
	}

	private static selectMostRecentDraft(results: readonly DraftReadResult[]): DraftSelection | null {
		const candidates = results.filter((result): result is DraftReadHit => result.kind === 'hit');

		if (candidates.length === 0) return null;

		let selectedCandidate = candidates[0];
		for (const candidate of candidates.slice(1)) {
			if (DraftClient.isPreferredSelectionCandidate(candidate, selectedCandidate)) {
				selectedCandidate = candidate;
			}
		}

		return { adapterIndex: selectedCandidate.adapterIndex, draft: selectedCandidate.draft };
	}

	private static isPreferredSelectionCandidate(candidate: DraftReadHit, current: DraftReadHit): boolean {
		if (candidate.draft.updatedAt > current.draft.updatedAt) return true;

		if (candidate.draft.updatedAt === current.draft.updatedAt && candidate.adapterIndex < current.adapterIndex) {
			return true;
		}

		return false;
	}

	private async repairPrimaryFromSelection(authorId: string, selected: DraftSelection): Promise<void> {
		const isPrimarySelection = selected.adapterIndex === 0;
		if (isPrimarySelection) return;

		const primaryAdapter = this.adapters[0];
		const selectedDraft = selected.draft;

		try {
			const primaryDraft = await primaryAdapter.getDraft(authorId);
			const isPrimaryDraftNewer = primaryDraft ? primaryDraft.updatedAt > selectedDraft.updatedAt : false;
			if (isPrimaryDraftNewer) return;

			await primaryAdapter.findOrCreateDraft(authorId);
			await primaryAdapter.updateDraft(authorId, selectedDraft);
		} catch {
			// best-effort repair only
		}
	}

	private async writeWithFallback<T>(operation: (adapter: DraftAdapter) => Promise<T>): Promise<T> {
		const errors: Error[] = [];

		for (const adapter of this.adapters) {
			try {
				// oxlint-disable-next-line no-await-in-loop
				const result = await operation(adapter);
				// TODO: should add some kind of debugging in development mode
				// console.log({ result, adapter: adapter.constructor.name });
				return result;
			} catch (error) {
				errors.push(error instanceof Error ? error : new Error(String(error)));
			}
		}

		throw new Error(`All adapters failed: ${errors.map((error) => error.message).join(' | ')}`);
	}
}
